import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Clean, single-file DownloadPage implementation
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.cubemeet.app';
const APK_URL = 'https://apk.cubemeet.app/app-release.apk';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const DownloadPage: React.FC = () => {
  const q = useQuery();
  const [copied, setCopied] = useState(false);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl || window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // noop
    }
  };

  const handlePlayClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)) {
      e.preventDefault();
      const intentUri = 'intent://details?id=com.cubemeet.app#Intent;scheme=market;package=com.android.vending;end';
      window.location.href = intentUri;
      setTimeout(() => window.open(PLAY_STORE_URL, '_blank', 'noopener'), 1400);
    }
  };

  return (
    <div className="cm-download-page" style={styles.page}>
      <a href="https://travel.cubemeet.app" className="cm-home-link" aria-label="Home">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M3 10.5L12 4l9 6.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 10.5v7a2 2 0 0 0 2 2h2.5V14a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5.5H17a2 2 0 0 0 2-2v-7" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>

      <style>{pageScopedCss}</style>

      <div style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.logoMain} className="logo-main">
            <div className="cube" aria-hidden>
              <div className="cube-face front">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`f-${i}`} className="logo-cell" />
                ))}
              </div>
              <div className="cube-face back">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`b-${i}`} className="logo-cell" />
                ))}
              </div>
              <div className="cube-face right">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`r-${i}`} className="logo-cell" />
                ))}
              </div>
              <div className="cube-face left">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`l-${i}`} className="logo-cell" />
                ))}
              </div>
              <div className="cube-face top">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`t-${i}`} className="logo-cell" />
                ))}
              </div>
              <div className="cube-face bottom">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`o-${i}`} className="logo-cell" />
                ))}
              </div>
            </div>
          </div>

          <h1 style={styles.title}>拼出精彩，游聚美好</h1>
          <p style={styles.subtitle}>團隊旅遊行程編輯與智能分賬平臺，每段旅程都是獨特拼圖，共同拼出完美體驗。</p>

          <div style={styles.ctaRow} className="download-cta">
            <a href={APK_URL} className="download-badge cta-btn cta-large" style={styles.apkButton}>
              <span className="badge-inner">
                <img src="/assets/images/apk_badge.png" alt="Download APK" className="cta-img" />
              </span>
            </a>

            <a
              href={PLAY_STORE_URL}
              onClick={handlePlayClick}
              className="play-badge cta-btn cta-large"
              style={styles.playBadge}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              id="google-play-btn"
            >
              <span className="badge-inner">
                <img src="/assets/images/google_play.png" alt="Get it on Google Play" className="cta-img" />
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const colors = ['#FF6B35', '#F7C548', '#2ED1A2', '#4A90E2', '#9013FE', '#F5A623', '#BD10E0', '#50E3C2', '#B8E986'];

const styles: { [k: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #FF7A4D 0%, #7A9BFF 100%)',
    fontFamily: "Poppins, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    color: '#fff',
    paddingTop: 72,
    boxSizing: 'border-box',
  },

  hero: {
    width: '100%',
    maxWidth: 420,
    padding: '18px 20px 48px',
    boxSizing: 'border-box',
    textAlign: 'center',
    minHeight: 'calc(100vh - 72px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logoMain: {
    marginBottom: 24,
    marginTop: 0,
  },
  cube: {
    width: 88,
    height: 88,
  },
  title: {
    fontSize: 30,
    fontWeight: 700,
    margin: '18px 0 12px',
    lineHeight: 1.05,
  },
  subtitle: {
    fontSize: 15,
    opacity: 0.95,
    marginBottom: 28,
    maxWidth: 380,
  },
  ctaRow: {
    display: 'flex',
    gap: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
    marginBottom: 20,
  },
  apkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0',
    background: 'transparent',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: 12,
    minWidth: 280,
    height: 80,
    justifyContent: 'center',
  },
  badgeContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  androidIcon: {
    fontSize: 28,
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg,#fff2,#fff0)',
    borderRadius: 8,
  },
  badgeText: {
    textAlign: 'left',
  },
  badgeLine1: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.2,
  },
  badgeLine2: {
    fontSize: 18,
    fontWeight: 700,
  },
  playBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    minWidth: 280,
    height: 80,
  },
};

const pageScopedCss = `
.cm-download-page .logo-main { perspective: 1200px; display:flex; justify-content:center; align-items:center; height:96px; }
.cm-download-page .cube { width: 88px; height: 88px; position: relative; transform-style: preserve-3d; animation: rotate-cube 16s linear infinite; }
.cm-download-page .cube:hover { animation-play-state: paused; }
@keyframes rotate-cube { from { transform: rotateX(0deg) rotateY(0deg); } to { transform: rotateX(360deg) rotateY(360deg); } }
.cm-download-page .cube-face { position:absolute; width:88px; height:88px; display:grid; grid-template-columns:repeat(3,1fr); grid-template-rows:repeat(3,1fr); box-sizing:border-box; background-color: rgba(0,0,0,0.06); }
.cm-download-page .logo-cell { width:100%; height:100%; border-radius:6px; box-shadow: inset 1px 1px 2px rgba(255,255,255,0.18), inset -1px -1px 2px rgba(0,0,0,0.08); }
.cm-download-page .front  { transform: rotateY(  0deg) translateZ(44px); }
.cm-download-page .back   { transform: rotateY(180deg) translateZ(44px); }
.cm-download-page .right  { transform: rotateY( 90deg) translateZ(44px); }
.cm-download-page .left   { transform: rotateY(-90deg) translateZ(44px); }
.cm-download-page .top    { transform: rotateX( 90deg) translateZ(44px); }
.cm-download-page .bottom { transform: rotateX(-90deg) translateZ(44px); }
/* colors for each cell position on a face (same sequence across faces) */
.cm-download-page .cube-face .logo-cell:nth-child(1) { background-color: #FF6B35; }
.cm-download-page .cube-face .logo-cell:nth-child(2) { background-color: #F7C548; }
.cm-download-page .cube-face .logo-cell:nth-child(3) { background-color: #2ED1A2; }
.cm-download-page .cube-face .logo-cell:nth-child(4) { background-color: #4A90E2; }
.cm-download-page .cube-face .logo-cell:nth-child(5) { background-color: #9013FE; }
.cm-download-page .cube-face .logo-cell:nth-child(6) { background-color: #F5A623; }
.cm-download-page .cube-face .logo-cell:nth-child(7) { background-color: #BD10E0; }
.cm-download-page .cube-face .logo-cell:nth-child(8) { background-color: #50E3C2; }
.cm-download-page .cube-face .logo-cell:nth-child(9) { background-color: #B8E986; }

.cm-download-page .download-badge { background: transparent; color:inherit; padding:0; border-radius:16px; display:inline-flex; align-items:center; justify-content:center; min-width:280px; height:80px; box-shadow: none; }
.cm-download-page .play-badge { display:inline-flex; align-items:center; justify-content:center; }
.cm-download-page .play-badge img { height:auto; width:auto; max-width:260px; display:block; object-fit:contain }
.cm-download-page .cta-large { min-width:280px; height:80px; border-radius:16px; padding:0 18px; display:inline-flex; align-items:center; justify-content:center }
.cm-download-page .badge-inner { width:200px; height:100%; display:flex; align-items:center; justify-content:center; padding:0 8px; box-sizing:border-box }
.cm-download-page .cta-img { width:100%; max-width:100%; height:auto; display:block; object-fit:contain }

/* Stronger rule to force both badge images to fill the badge-inner width
   This overrides other img rules (like .play-badge img) that were setting width:auto. */
.cm-download-page .cta-large .badge-inner .cta-img {
  max-width:100%;
  height:auto;
  max-height:64px;
  display:block;
  object-fit:contain;
}

/* ensure CTA container centers and badges align */
.cm-download-page .download-cta { display:flex; justify-content:center; align-items:center; gap:12px; }
.cm-download-page .play-badge { display:inline-flex; align-items:center; justify-content:center; }
.cm-download-page .play-badge img { display:block; }

/* Home link top-left */
.cm-home-link { position:fixed; left:18px; top:18px; z-index:1200; display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:8px; background: rgba(255,255,255,0.08); box-shadow: 0 6px 18px rgba(0,0,0,0.12); }
.cm-home-link svg { display:block; }

@media (max-width: 480px) {
  .cm-download-page .download-cta { flex-direction: column; gap: 14px; }
    .cm-download-page .cta-large { min-width: 0; width: 82%; max-width: 420px; height:68px; }
      .cm-download-page .cta-img { /* mobile: allow width to control size */ }
      .cm-download-page .play-badge img { width: auto; max-width: 80%; height: auto; }
      /* Make badge-inner fluid on small screens so images scale with viewport */
      .cm-download-page .badge-inner { width: 82%; max-width: 420px; }
      /* Ensure the .cta-img fills the mobile badge-inner width as well */
      .cm-download-page .cta-large .badge-inner .cta-img { max-width:100%; }
}
`;

export default DownloadPage;